/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import axios from 'axios';
import { i18n } from 'vue-lang-router';

const state = () => ({
  // Product Categories
  productCategories: [],
  productCategoriesLoading: false,
  productCategoriesError: '',
  selectedProductCategory: Object,
  // Product Groups
  productGroups: [],
  productGroupsLoading: false,
  productGroupsError: '',
  selectedProductGroup: Object,
  // Material Types
  materialTypes: [],
  materialTypesLoading: false,
  materialTypesError: '',
  selectedMaterialType: Object,
  materials: [],
  materialsLoading: false,
  materialsLoadingMore: false,
  materialsCurrentPage: 1,
  materialsTotalPages: null,
  materialsReachedTheEnd: false,
  materialsError: '',
});

const getters = {
  // Product Categories
  productCategories: (state) => state.productCategories,
  productCategoriesLoading: (state) => state.productCategoriesLoading,
  productCategoriesError: (state) => state.productCategoriesError,
  selectedProductCategory: (state) => state.selectedProductCategory,
  // Product Groups
  productGroups: (state) => state.productGroups,
  productGroupsLoading: (state) => state.productGroupsLoading,
  productGroupsError: (state) => state.productGroupsError,
  selectedProductGroup: (state) => state.selectedProductGroup,
  // Material Types
  materialTypes: (state) => state.materialTypes,
  materialTypesLoading: (state) => state.materialTypesLoading,
  materialTypesError: (state) => state.materialTypesError,
  selectedMaterialType: (state) => state.selectedMaterialType,
  // Materials
  materials: (state) => state.materials,
  materialsLoading: (state) => state.materialsLoading,
  materialsLoadingMore: (state) => state.materialsLoadingMore,
  materialsCurrentPage: (state) => state.materialsCurrentPage,
  materialsTotalPages: (state) => state.materialsTotalPages,
  materialsReachedTheEnd: (state) => state.materialsReachedTheEnd,
  materialsError: (state) => state.materialsError,
};

const actions = {
  getProductCategories(state) {
    state.commit('setProductCategories');
    const params = {
      lang: i18n.locale,
      parent: 0,
    };
    axios.get(`${process.env.VUE_APP_API_URL}/material_categories`, { params })
      .then((response) => state.commit('setProductCategoriesSuccess', response.data))
      .catch(() => state.commit('setProductCategoriesFailure', i18n.t('common.connectionError')));
  },
  getProductGroups(state) {
    state.commit('setProductGroups');
    const params = {
      lang: i18n.locale,
      parent: state.getters.selectedProductCategory.id,
      hide_empty: true,
    };
    axios.get(`${process.env.VUE_APP_API_URL}/material_categories`, { params })
      .then((response) => state.commit('setProductGroupsSuccess', response.data))
      .catch(() => state.commit('setProductGroupsFailure', i18n.t('common.connectionError')));
  },
  getMaterialTypes(state) {
    state.commit('setMaterialTypes');
    const params = {
      lang: i18n.locale,
      parent: state.getters.selectedProductGroup.id,
      hide_empty: true,
    };
    axios.get(`${process.env.VUE_APP_API_URL}/material_categories`, { params })
      .then((response) => state.commit('setMaterialTypesSuccess', response.data))
      .catch(() => state.commit('setMaterialTypesFailure', i18n.t('common.connectionError')));
  },
  getMaterials(state) {
    state.commit('setMaterials');
    const paginationParams = {
      page: state.getters.materialsCurrentPage,
      per_page: 10,
    };
    const params = {
      lang: i18n.locale,
      material_categories: state.getters.selectedMaterialType.id,
      ...paginationParams,
    };
    axios.get(`${process.env.VUE_APP_API_URL}/materials`, { params })
      .then((response) => state.commit('setMaterialsSuccess', response))
      .catch(() => state.commit('setMaterialsFailure', i18n.t('common.connectionError')));
  },
  reset(state) {
    state.commit('reset');
  },
};

const mutations = {
  // Product Categories
  setProductCategories(state) {
    state.productCategoriesLoading = true;
  },
  setProductCategoriesSuccess(state, payload) {
    state.productCategories = payload;
    state.productCategoriesLoading = false;
  },
  setProductCategoriesFailure(state, payload) {
    state.productCategoriesLoading = false;
    state.productCategoriesError = payload;
  },
  setSelectedProductCategory(state, payload) {
    state.selectedProductCategory = payload;
  },
  // Product Groups
  setProductGroups(state) {
    state.productGroupsLoading = true;
  },
  setProductGroupsSuccess(state, payload) {
    state.productGroups = payload;
    state.productGroupsLoading = false;
  },
  setProductGroupsFailure(state, payload) {
    state.productGroupsLoading = false;
    state.productGroupsError = payload;
  },
  setSelectedProductGroup(state, payload) {
    state.selectedProductGroup = payload;
  },
  clearProductGroups(state) {
    state.productGroups = [];
    state.selectedProductGroup = Object;
  },
  // Material Types
  setMaterialTypes(state) {
    state.materialTypesLoading = true;
  },
  setMaterialTypesSuccess(state, payload) {
    state.materialTypes = payload;
    state.materialTypesLoading = false;
  },
  setMaterialTypesFailure(state, payload) {
    state.materialTypesLoading = false;
    state.materialTypesError = payload;
  },
  setSelectedMaterialType(state, payload) {
    state.selectedMaterialType = payload;
  },
  clearMaterialTypes(state) {
    state.materialTypes = [];
    state.selectedMaterialType = Object;
  },
  // Materials
  setMaterials(state) {
    state.materialsError = '';
    if (state.materialsLoadingMore) {
      state.materialsCurrentPage += 1;
    } else {
      state.materials = [];
      state.materialsLoading = true;
      state.materialsCurrentPage = 1;
    }
  },
  setMaterialsSuccess(state, payload) {
    if (state.materialsLoadingMore) {
      state.materials = state.materials.concat(payload.data);
    } else {
      state.materials = payload.data;
      state.materialsTotalPages = Number(payload.headers['x-wp-totalpages']);
    }
    state.materialsReachedTheEnd = state.materialsCurrentPage === state.materialsTotalPages;
    state.materialsLoading = false;
    state.materialsLoadingMore = false;
  },
  setMaterialsFailure(state, payload) {
    state.materialsLoading = false;
    state.materialsLoadingMore = false;
    state.materials = [];
    state.materialsError = payload;
  },
  setLoadingMore(state, payload) {
    state.materialsLoadingMore = payload;
  },
  reset(state) {
    state.selectedProductCategory = Object;
    state.productGroups = [];
    state.selectedProductGroup = Object;
    state.materialTypes = [];
    state.selectedMaterialType = Object;
    state.materials = [];
    state.materialsCurrentPage = 1;
    state.materialsTotalPages = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
